import React from 'react';
import { ArrowRight, Play, Check, Star, Shield, Award } from 'lucide-react';
import { useLanguage } from '../../../language/hooks/useLanguageHook';
import { heroFeatures } from '../../data/landingPageData'; // Import hero features data

const HeroSection = ({ onStartFreeTrial, onOpenVideoModal }) => {
  const { t } = useLanguage();

  return (
    <div className="relative pt-20 px-6"> {/* Removed min-h-screen, handled by parent */}
      <div className="max-w-7xl mx-auto py-24">
        {/* Optional Enhanced Trust Badge (uncomment if needed) */}
        {/*
        <div className="flex items-center justify-center mb-8 space-x-4">
          <div className="flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full animate-fade-in">
            <Shield className="h-5 w-5" />
            <span className="text-sm font-medium">
              {t("public.landing.hero.trustBadges.restaurants")}
            </span>
          </div>
          <div className="flex items-center space-x-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full animate-fade-in delay-100">
            <Star className="h-5 w-5" />
            <span className="text-sm font-medium">
              {t("public.landing.hero.trustBadges.rating")}
            </span>
          </div>
          <div className="flex items-center space-x-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-full animate-fade-in delay-200">
            <Award className="h-5 w-5" />
            <span className="text-sm font-medium">
              {t("public.landing.hero.trustBadges.position")}
            </span>
          </div>
        </div>
        */}

        {/* Split Hero Layout */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="scroll-fade-in"> {/* Added scroll animation class */}
            <h1 className="text-6xl font-bold text-gray-900 mb-8 flex flex-col items-start hero-text">
              <span className="ml-0">{t("public.landing.hero.title.line1")}</span>
              <span className="ml-8">{t("public.landing.hero.title.line2")}</span>
              <span className="ml-16 text-blue-600 relative inline-block">
                {t("public.landing.hero.title.line3")}
                <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8">
                  <path d="M0 4C50 4 50 1 100 1S150 7 200 7" fill="none" stroke="#2563EB" strokeWidth="2" />
                </svg>
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 ml-16 max-w-xl hero-description">
              {t("public.landing.hero.description")}
            </p>

            <div className="flex space-x-4 mb-8">
              <button
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg flex items-center backdrop-blur-sm"
                onClick={onStartFreeTrial} // Use prop
              >
                {t("public.landing.hero.buttons.trial")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button
                className="bg-white/80 text-gray-900 px-8 py-4 rounded-lg border-2 border-gray-200 hover:border-blue-600 transition-all duration-300 ease-in-out flex items-center backdrop-blur-sm group"
                onClick={onOpenVideoModal} // Use prop
              >
                <div className="bg-blue-100 rounded-full p-1 mr-2 group-hover:bg-blue-200 transition-colors duration-300 ease-in-out">
                  <Play className="h-4 w-4 text-blue-600" />
                </div>
                {t("public.landing.hero.buttons.demo")}
              </button>
            </div>

            <div className="flex items-center space-x-6">
              {heroFeatures.map(({ key }) => (
                <div key={key} className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  {t(key)} {/* Translate using the key from data file */}
                </div>
              ))}
            </div>
          </div>

          {/* Animated Product Preview */}
          <div className="relative scroll-scale-in"> {/* Added scroll animation class */}
            <div className="bg-white rounded-xl shadow-2xl p-6 transform hover:scale-[1.02] transition-transform duration-300 ease-in-out"> {/* Smoother hover scale */}
              {/* Simplified pulse animation for placeholder */}
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded mb-4 w-3/4"></div>
                <div className="space-y-3 mb-6">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {"300%"} {/* Hardcoded value */}
                    </div>
                  <div className="text-sm text-gray-600">
                    {t("public.landing.hero.preview.stats.fasterResponse")} {/* Corrected key */}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {"1000+"} {/* Hardcoded value */}
                    </div>
                  <div className="text-sm text-gray-600">
                    {t("public.landing.hero.preview.stats.automatedResponses")} {/* Corrected key */}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                     {"10hrs+"} {/* Hardcoded value */}
                     </div>
                  <div className="text-sm text-gray-600">
                    {t("public.landing.hero.preview.stats.timeSavedWeekly")} {/* Corrected key */}
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium animate-bounce">
              {t("public.landing.hero.preview.aiPowered")}
            </div>
          </div>
        </div>

        {/* Optional Trusted By Logos (uncomment and import data if needed) */}
        {/*
        <div className="text-center mt-24 scroll-fade-in">
          <p className="text-sm text-gray-500 mb-4">
            {t("public.landing.trustedBy.title")}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all">
            {trustedByLogos.map((logo, index) => (
              <img
                key={index}
                src={logo} // Assuming logo paths are correct in landingPageData.js
                alt={t("public.landing.trustedBy.logoAlt", { index: index + 1 })}
                className="h-10 md:h-12 object-contain"
              />
            ))}
          </div>
        </div>
        */}
      </div>
    </div>
  );
};

export default HeroSection; 